import React, { useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Col, Row, CardBody, Card } from 'reactstrap';
import RadioSelectTable from 'src/shared/components/table/RadioSelectTable';

// Helper: extract the last part of a comma-separated address as the "Emirate"
const extractEmirate = (address) =>
  address ? address.split(',').pop()?.trim() || 'Unknown' : 'Unknown';

// Column definitions for the Hub list
const HUB_COLUMNS = [
  {
    key: 'hubName',
    header: 'Hub Name',
    sortable: true,
  },
  {
    key: 'hubNo',
    header: 'Hub No.',
    width: '150px',
    sortable: true,
  },
  {
    key: 'address',
    header: 'Emirates',
    width: '140px',
    sortable: false,
    render: (value) => {
      const emirate = extractEmirate(value);
      // Assign a color per emirate name for visual distinction
      const colors = {
        'Dubai': { bg: '#eff6ff', border: '#bfdbfe', text: '#1d4ed8' },
        'Abu Dhabi': { bg: '#f0fdf4', border: '#bbf7d0', text: '#15803d' },
        'Sharjah': { bg: '#fff7ed', border: '#fed7aa', text: '#c2410c' },
        'Ajman': { bg: '#fdf4ff', border: '#e9d5ff', text: '#7e22ce' },
        'Fujairah': { bg: '#fff1f2', border: '#fecdd3', text: '#be123c' },
        'Ras Al Khaimah': { bg: '#fefce8', border: '#fef08a', text: '#a16207' },
        'Umm Al Quwain': { bg: '#f0fdfa', border: '#99f6e4', text: '#0f766e' },
      };
      const style = colors[emirate] || { bg: '#f8fafc', border: '#e2e8f0', text: '#475569' };
      return (
        <span style={{
          display: 'inline-block',
          padding: '2px 10px',
          borderRadius: '12px',
          fontSize: '0.72rem',
          fontWeight: 700,
          letterSpacing: '0.3px',
          backgroundColor: style.bg,
          border: `1px solid ${style.border}`,
          color: style.text,
        }}>
          {emirate}
        </span>
      );
    },
  },
];

const HubDetailsView = ({ reactTableData, isNoRecordFound, onAdd }) => {
  const rows = reactTableData?.tableRowsData ?? [];
  const [selectedHub, setSelectedHub] = useState(rows.length > 0 ? rows[0] : null);
  const [selectedEmirate, setSelectedEmirate] = useState('All');

  // Build unique Emirates list from data
  const emirates = useMemo(() => {
    const unique = [...new Set(rows.map(r => extractEmirate(r.address)))].sort();
    return ['All', ...unique];
  }, [rows]);

  // Pre-filter rows by selected Emirate
  const filteredRows = useMemo(() => {
    if (selectedEmirate === 'All') return rows;
    return rows.filter(r => extractEmirate(r.address) === selectedEmirate);
  }, [rows, selectedEmirate]);

  // Chip color
  const chipColors = {
    'Dubai': '#1d4ed8',
    'Abu Dhabi': '#15803d',
    'Sharjah': '#c2410c',
    'Ajman': '#7e22ce',
    'Fujairah': '#be123c',
    'Ras Al Khaimah': '#a16207',
    'Umm Al Quwain': '#0f766e',
  };

  return (
    <Col md={12} lg={12} style={{ marginTop: '20px' }}>
      <Row>
        {/* ── Left: Master List ───────────────────────────── */}
        <Col md={7} lg={7}>

          {/* Emirates Filter Chips */}
          {!isNoRecordFound && emirates.length > 1 && (
            <div
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: '8px',
                marginBottom: '12px',
                alignItems: 'center',
              }}
            >
              <span style={{ fontSize: '0.75rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px', marginRight: '4px' }}>
                Emirates:
              </span>
              {emirates.map(em => {
                const isActive = selectedEmirate === em;
                const color = em === 'All' ? '#6366f1' : (chipColors[em] || '#475569');
                return (
                  <button
                    key={em}
                    onClick={() => setSelectedEmirate(em)}
                    style={{
                      padding: '4px 14px',
                      borderRadius: '20px',
                      border: `1.5px solid ${isActive ? color : '#e2e8f0'}`,
                      backgroundColor: isActive ? color : '#ffffff',
                      color: isActive ? '#ffffff' : '#64748b',
                      fontSize: '0.78rem',
                      fontWeight: isActive ? 700 : 500,
                      cursor: 'pointer',
                      outline: 'none',
                      transition: 'all 0.15s ease',
                      boxShadow: isActive ? `0 2px 8px ${color}40` : 'none',
                    }}
                  >
                    {em}
                  </button>
                );
              })}
            </div>
          )}

          <RadioSelectTable
            rows={filteredRows}
            columns={HUB_COLUMNS}
            rowKey="hubId"
            selectedKey={selectedHub?.hubId}
            onSelect={setSelectedHub}
            searchKeys={['hubName', 'hubNo']}
            searchPlaceholder="Search by Hub Name or No..."
            sortKey="hubName"
            headerTitle="Hub List"
            onAdd={onAdd}
            pageSizes={[5, 10, 20, 50]}
          />
        </Col>

        {/* ── Right: Detail Panel ─────────────────────────── */}
        <Col md={5} lg={5}>
          <Card style={{
            borderRadius: '12px',
            border: '1px solid #e2e8f0',
            boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)',
            minHeight: '520px',
            background: '#ffffff',
            overflow: 'hidden',
          }}>
            {selectedHub ? (
              <>
                {/* Header Banner */}
                <div style={{
                  background: 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)',
                  padding: '24px 20px',
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div>
                      <h4 style={{ margin: 0, fontWeight: 800, fontSize: '1.2rem', color: '#ffffff' }}>
                        {selectedHub.hubName}
                      </h4>
                      <span style={{ fontSize: '0.85rem', color: 'rgba(255,255,255,0.85)' }}>
                        Hub No: {selectedHub.hubNo}
                      </span>
                    </div>
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                      <span
                        className={`badge ${selectedHub.status ? 'bg-success' : 'bg-danger'}`}
                        style={{ fontSize: '0.75rem', fontWeight: 700 }}
                      >
                        {selectedHub.status ? 'ACTIVE' : 'IN-ACTIVE'}
                      </span>
                      
                    </div>
                  </div>
                </div>

                {/* Details Body */}
                <CardBody style={{ padding: '20px' }}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>

                    {/* Contact Details */}
                    <div>
                      <h6 className="text-muted text-uppercase fw-bold mb-2" style={{ fontSize: '0.7rem', letterSpacing: '0.5px' }}>
                        Contact Details
                      </h6>
                      <div style={{ background: '#f8fafc', borderRadius: '8px', padding: '12px 14px', border: '1px solid #f1f5f9', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        <div className="d-flex justify-content-between">
                          <span className="text-muted small">Person Name:</span>
                          <span className="fw-bold small text-dark">{selectedHub.contactPersonName || '—'}</span>
                        </div>
                        <div className="d-flex justify-content-between">
                          <span className="text-muted small">Mobile No:</span>
                          <span className="fw-bold small text-dark">{selectedHub.contactPersonMobileNo || '—'}</span>
                        </div>
                        <div className="d-flex justify-content-between">
                          <span className="text-muted small">Tech Email:</span>
                          <span className="fw-bold small text-dark">{selectedHub.techSupportEmailId || '—'}</span>
                        </div>
                      </div>
                    </div>

                    {/* Operations & SLAs */}
                    <div>
                      <h6 className="text-muted text-uppercase fw-bold mb-2" style={{ fontSize: '0.7rem', letterSpacing: '0.5px' }}>
                        Operations & SLAs
                      </h6>
                      <div style={{ background: '#f8fafc', borderRadius: '8px', padding: '12px 14px', border: '1px solid #f1f5f9', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        <div className="d-flex justify-content-between">
                          <span className="text-muted small">Express Delivery Duration:</span>
                          <span className="fw-bold small text-dark">
                            {selectedHub.expressDeliveryWithIn ? `${selectedHub.expressDeliveryWithIn} Mins` : '—'}
                          </span>
                        </div>
                        <div className="d-flex justify-content-between">
                          <span className="text-muted small">Allowed Distance:</span>
                          <span className="fw-bold small text-dark">
                            {selectedHub.allowedDistanceForDelivery ? `${selectedHub.allowedDistanceForDelivery} Km` : '—'}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Geographic Info */}
                    <div>
                      <h6 className="text-muted text-uppercase fw-bold mb-2" style={{ fontSize: '0.7rem', letterSpacing: '0.5px' }}>
                        Geographic Info
                      </h6>
                      <div style={{ background: '#f8fafc', borderRadius: '8px', padding: '12px 14px', border: '1px solid #f1f5f9', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        <div className="d-flex justify-content-between">
                          <span className="text-muted small">Emirates:</span>
                          <span className="fw-bold small text-dark">{extractEmirate(selectedHub.address)}</span>
                        </div>
                        <div className="d-flex justify-content-between">
                          <span className="text-muted small">Latitude:</span>
                          <span className="fw-bold small text-dark">{selectedHub.latitude || '—'}</span>
                        </div>
                        <div className="d-flex justify-content-between">
                          <span className="text-muted small">Longitude:</span>
                          <span className="fw-bold small text-dark">{selectedHub.longitude || '—'}</span>
                        </div>
                        <div className="mt-2 pt-2" style={{ borderTop: '1px solid #e2e8f0' }}>
                          <span className="text-muted small d-block mb-1">Full Address:</span>
                          <span className="small fw-semibold text-dark d-block" style={{ lineHeight: '1.4' }}>
                            {selectedHub.address || '—'}
                          </span>
                        </div>
                      </div>
                    </div>

                  </div>
                </CardBody>
              </>
            ) : (
              <div style={{
                display: 'flex', flexDirection: 'column', alignItems: 'center',
                justifyContent: 'center', padding: '40px', textAlign: 'center', minHeight: '520px',
              }}>
                <div style={{ fontSize: '3.5rem', color: '#cbd5e1', marginBottom: '16px' }}>🏢</div>
                <h5 className="fw-bold text-muted">No Hub Selected</h5>
                <p className="text-muted small">Select a hub from the list to view details.</p>
              </div>
            )}
          </Card>
        </Col>
      </Row>
    </Col>
  );
};

HubDetailsView.propTypes = {
  reactTableData: PropTypes.shape({
    tableRowsData: PropTypes.arrayOf(PropTypes.shape()),
  }),
  isNoRecordFound: PropTypes.bool,
  onAdd:  PropTypes.func,
  onEdit: PropTypes.func,
};

export default HubDetailsView;
